/**
 * First we will load all of this project's JavaScript dependencies which
 * includes Vue and other libraries. It is a great starting point when
 * building robust, powerful web applications using Vue and Laravel.
 */
import './bootstrap';

import 'babel-polyfill';
import 'malihu-custom-scrollbar-plugin/jquery.mCustomScrollbar.css';
import 'malihu-custom-scrollbar-plugin/jquery.mCustomScrollbar.concat.min.js';
import '@mdi/font/css/materialdesignicons.css';
// Cropper CSS will be imported conditionally when needed

import Vue from 'vue';

window.Vue = Vue;

import VueRouter from 'vue-router';

import store from './store';
import Routes from './routes';

// Full Calendar
import FullCalendar from 'vue-full-calendar';
Vue.use(FullCalendar);

// Vee Validate
import VeeValidate from 'vee-validate';
import { Validator } from 'vee-validate';
import msgEn from 'vee-validate/dist/locale/en';
import msgEs from 'vee-validate/dist/locale/es';
import msgPt from 'vee-validate/dist/locale/pt_BR';
import msgRu from 'vee-validate/dist/locale/ru';
import msgDe from 'vee-validate/dist/locale/de';
import msgFr from 'vee-validate/dist/locale/fr';
import msgIt from 'vee-validate/dist/locale/it';
import msgJa from 'vee-validate/dist/locale/ja';
import msgZhCN from 'vee-validate/dist/locale/zh_CN';
import msgZhTW from 'vee-validate/dist/locale/zh_TW';
import msgVi from 'vee-validate/dist/locale/vi';
import { CONST } from './constant';
import UTILS from './utils/utils';

import VueClipboard from 'vue-clipboard2';
Vue.use(VueClipboard);

Vue.use(VeeValidate);
const msgLangs = {
  en: msgEn,
  es: msgEs,
  pt: msgPt,
  ru: msgRu,
  de: msgDe,
  fr: msgFr,
  it: msgIt,
  ja: msgJa,
  zh_CN: msgZhCN,
  zh_TW: msgZhTW,
  vi: msgVi,
};

// Global variables will be initialized by the JS files loaded in index.html

// Initialize validation (use fallback if window.$commin is not available)
const userLang = (window.$commin?.user?.lang) || 'en';
const dictAttributes = (window.$commin?.const?.dict?.attributes) || {};

Validator.localize(userLang, {
  messages: msgLangs[userLang]?.messages || msgLangs.en.messages,
  attributes: dictAttributes,
});

import VueMoment from 'vue-moment';
import moment from 'moment-timezone';
const langFile = userLang.toLowerCase().replace('_', '-');
if (langFile !== 'en') {
  try {
    require('moment/locale/' + langFile);
  } catch (e) {
    console.warn('Moment locale not found for:', langFile);
  }
}
moment.locale(langFile);
if (store.state.user?.timezone) {
  moment.tz.setDefault(store.state.user.timezone);
}
Vue.use(VueMoment, {
  moment,
});

import Spinner from 'vue-simple-spinner';
Vue.component('vue-simple-spinner', Spinner);

import VueQrcode from '@chenfengyuan/vue-qrcode';
Vue.component(VueQrcode.name, VueQrcode);

import VueQrcodeReader from 'vue-qrcode-reader';
Vue.use(VueQrcodeReader);

import Vuetify from 'vuetify';
import 'vuetify/dist/vuetify.min.css';
Vue.use(Vuetify, {
  theme: {
    primary: '#388E3C',
    secondary: '#1B5E20',
    accent: '#E8F5E9',
    error: '#FF5252',
    danger: '#E64A19',
    icon: '#888888',
    btn: '#2E7D32',
    form: '#388E3C',
  },
});

import Inview from 'vueinview';
Vue.use(Inview);

/**
 * Next, we will create a fresh Vue application instance and attach it to
 * the page. Then, you may begin adding components to this application
 * or customize the JavaScript scaffolding to fit your unique needs.
 */
Vue.use(VueRouter);

// Vue Router 3: a beforeEach redirect (e.g. the guest guard below appending `/{lang}/guest`) rejects the
// navigation promise with a "Redirected ... via a navigation guard" NavigationFailure. The redirect still
// succeeds (the page renders), but without catching the rejection it is uncaught and the webpack dev-server
// error overlay shows it. Swallow benign NavigationFailures (Redirected / Duplicated) on push & replace.
['push', 'replace'].forEach((method) => {
  const original = VueRouter.prototype[method];
  VueRouter.prototype[method] = function (location, onResolve, onReject) {
    if (onResolve || onReject) return original.call(this, location, onResolve, onReject);
    return original.call(this, location).catch((err) => {
      const benign = VueRouter.isNavigationFailure
        ? VueRouter.isNavigationFailure(err)
        : /redirected|redundant navigation|navigation guard/i.test((err && err.message) || '');
      if (benign) return err; // expected redirect/duplicate — not an error
      return Promise.reject(err);
    });
  };
});

const router = new VueRouter({
  routes: Routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    }
    return null;
  },
  mode: 'history',
});

// Initialize store (use fallback if window.$commin is not available)
const communities = (window.$commin?.communities) || [];
const communityUpdatedAt = (window.$commin?.communityUpdatedAt) || Date.now();

store.commit('COM_REFRESH', { cs: communities, updatedTime: communityUpdatedAt });
if (communities.length > 0) {
  store.commit('COM_SELECT_COMMUNITY', communities[0]);
}

router.beforeEach((to, from, next) => {
  if (window.$G) {
    let path = to.path;
    if (!path.endsWith('/guest') && !path.endsWith('/guest/')) {
      path = path + (path.endsWith('/') ? '' : '/') + window.$L + '/guest';
      return next({ path: path });
    }
    const pathes = path.split('/');
    if (pathes.length < 4 || pathes[2] !== String(window.$G)) {
      console.warn(`invalid access to ${path}`);
      if (window.$T) {
        store.commit('OPEN_SNACKBAR', { msg: window.$T('common.not_for_guest'), danger: true });
      }
      return next({ path: `/home/${window.$G}/${window.$L}/guest` });
    }
  }
  const cMap = store.state.community?.communityMap || {};
  let cid = to.params.id;
  if (!cid) cid = 0;
  const c = cMap[cid];
  if (!c && communities.length > 0) {
    // Issue #12/#13: 覗いてみる(/...../guest) を会員ホーム(/)へリダイレクトしない。
    // ログイン会員が「コミュニティを覗いてみる」を開くと /home/{cid}/{lang}/guest（未所属コミュ）に来るが、
    // ここで / へ飛ばすと会員自身の内容（最後のCID・友だち申請）が出て peek(c-peek/ヘッダ)も外れていた。
    // 覗いてみるはゲストビューのまま維持する。
    // ※根本修正は BE TopPageController が /guest URL をログイン時もゲストモードで描画すること。これは、
    //   万一 seed が会員化しても peek を会員ホームへ弾かないための FE 安全網。
    const isGuestUrl = to.path.endsWith('/guest') || to.path.endsWith('/guest/');
    if (!isGuestUrl) {
      return next({ path: '/' });
    }
  }
  if (c) {
    store.commit('COM_SELECT_COMMUNITY', c);
  }
  next();
});

// Helper functions for managing root class
function setRootClass(className) {
  const root = document.documentElement;
  if (!root.classList.contains(className)) {
    root.classList.add(className);
  }
}

function removeRootClass(className) {
  const root = document.documentElement;
  root.classList.remove(className);
}

router.afterEach((to, from) => {
  store.commit('SET_HELP', true);

  // set chat background
  if (to.name === 'talkDetail') setRootClass('chat');
  else removeRootClass('chat');

  // set page titles
  const title = store.state.tools?.pageTitle;
  if (title) {
    store.commit('PAGE_TITLE', null);
    return UTILS.setDocumentTitle(to, null, title);
  }

  const id = to.params.id;
  if (!id) return UTILS.setDocumentTitle(to, null);
  const numId = Number(id);
  const curC = store.state.community?.selectedCommunity;
  if (curC && curC.id === numId) return UTILS.setDocumentTitle(to, curC);

  const c = store.state.community?.communityMap?.[numId];
  if (c) {
    store.commit('COM_SELECT_COMMUNITY', c);
    return UTILS.setDocumentTitle(to, c);
  }
  const fallbackC = store.state.community?.communities?.[0];
  console.warn('not matched in current communities id [' + numId + ']');
  if (fallbackC) {
    store.commit('COM_SELECT_COMMUNITY', fallbackC);
  }
  document.title = to.meta.title ? (window.$T ? window.$T(to.meta.title) : to.meta.title) + ' - commin' : 'commin';
  return UTILS.setDocumentTitle(to, null);
});

// GA(UA-115205170-2) と AdSense は本番ホスト(commin.app)でのみ作動させる。stg.commin.app / dev / ローカルは
// 同一バンドルだが prod 解析を汚さず・実広告も配信しない（dist は環境非依存なので host で判定。issue#35 stg 分離）。
import VueAnalytics from 'vue-analytics';
import Ads from 'vue-google-adsense';
if (UTILS.isProdHost()) {
  Vue.use(VueAnalytics, {
    id: 'UA-115205170-2',
    router,
  });

  Vue.use(require('vue-script2'));
  Vue.use(Ads.Adsense);
  Vue.use(Ads.InArticleAdsense);
  Vue.use(Ads.InFeedAdsense);
}

// Import and register components
const common = './components/common';
Vue.component('root', () => import('./components/RootComponent.vue'));
Vue.component('error-box', () => import(`${common}/ErrorBox.vue`));
Vue.component('msg-dialog', () => import(`${common}/MsgDialog.vue`));
Vue.component('confirm-dialog', () => import(`${common}/ConfirmDialog.vue`));
Vue.component('balloon', () => import(`${common}/Balloon.vue`));
Vue.component('select-community', () => import(`${common}/SelectCommunity.vue`));
Vue.component('select-community-s', () => import(`${common}/SelectCommunityS.vue`));
Vue.component('select-groups-members', () => import(`${common}/SelectGroupsMembers.vue`));
Vue.component('select-groups-members-field', () => import(`${common}/SelectGroupsMembersField.vue`));
Vue.component('select-community-header', () => import(`${common}/SelectCommunityHeader.vue`));
Vue.component('selected-members', () => import(`${common}/SelectedMembers.vue`));
Vue.component('cmid-avatar', () => import(`${common}/CmidAvatar.vue`));
Vue.component('cm-list', () => import(`${common}/CmList.vue`));
Vue.component('cm-name', () => import(`${common}/CmName.vue`));
Vue.component('single-crop', () => import(`${common}/SingleCrop.vue`));
Vue.component('tail-loader', () => import(`${common}/TailLoader.vue`));
Vue.component('community-chip-s', () => import(`${common}/CommunityChipS.vue`));
Vue.component('community-member-chip-s', () => import(`${common}/CommunityMemberChipS.vue`));
Vue.component('community-member-avatar', () => import(`${common}/CommunityMemberAvatar.vue`));
Vue.component('spinner', () => import(`${common}/Spinner.vue`));

// Custom components (placeholder - would need actual implementation)
if (window.VueCustomImgComponent) {
  Vue.component(window.VueCustomImgComponent.name, window.VueCustomImgComponent);
}
if (window.VueCustomSvgComponent) {
  Vue.component(window.VueCustomSvgComponent.name, window.VueCustomSvgComponent);
}

import Commin from './utils/plugin/commin';
Vue.use(Commin);

import db from './firebase/init';
Vue.prototype.$firestoreDb = db;

// Create Vue app instance
const app = new Vue({
  el: '#root',
  store: store,
  router: router,
});

// jQuery event handlers (with safety checks)
if (typeof $ !== 'undefined') {
  $(function() {
    $(document).on('keypress', 'input:not(.allow_submit)', function(event) {
      return event.which !== 13;
    });
  });
}

// Window resize handler
let timer = 0;
window.onresize = function() {
  if (timer > 0) {
    clearTimeout(timer);
  }

  timer = setTimeout(function() {
    store.commit('SET_WINDOW_SIZE');
    if (typeof $ !== 'undefined') {
      $('.lside').mCustomScrollbar('update');
    }
  }, 100);
};

// Scroll event handler
let scrollEvent = null;
window.onscroll = function() {
  if (scrollEvent) {
    return;
  }
  scrollEvent = setTimeout(function() {
    store.commit('SCROLLED');
    scrollEvent = null;
  }, 50);
};

export default app;